import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api, getCategoryLabel, getCategoryColor, formatDate } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Clock, Calendar, Users, ArrowLeft, Send, CheckCircle, XCircle, MessageCircle, Star, Flag, ExternalLink, Trash2, Pencil } from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function OpportunityDetails() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [opp, setOpp] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [message, setMessage] = useState('');
  const [applications, setApplications] = useState([]);
  const [hasApplied, setHasApplied] = useState(false);
  const [loading, setLoading] = useState(true);
  // Review state
  const [showReview, setShowReview] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewedApps, setReviewedApps] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const oppRes = await api.get(`/opportunities/${id}`);
      setOpp(oppRes.data);
      if (user?.role === 'organization' && oppRes.data.creator_id === user.user_id) {
        const appsRes = await api.get('/applications');
        const oppApps = appsRes.data.filter(a => a.opp_id === id);
        setApplications(oppApps);
        // Check which completed applicants already have reviews
        const completedApps = oppApps.filter(a => a.status === 'completed');
        if (completedApps.length > 0) {
          const reviewed = new Set();
          for (const app of completedApps) {
            const revRes = await api.get(`/reviews/${app.applicant_id}`);
            const hasReview = revRes.data.some(r => r.opp_id === id && r.reviewer_id === user.user_id);
            if (hasReview) reviewed.add(app.app_id);
          }
          setReviewedApps(reviewed);
        }
      }
      if (user?.role === 'teen') {
        const appsRes = await api.get('/applications');
        setHasApplied(appsRes.data.some(a => a.opp_id === id));
      }
    } catch {
      toast.error('Failed to load opportunity');
    }
    setLoading(false);
  };

  const handleApply = async () => {
    try {
      await api.post(`/opportunities/${id}/apply`, { message });
      setShowApply(false);
      setHasApplied(true);
      toast.success('Application sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    }
  };

  const handleApplicationStatus = async (appId, status) => {
    try {
      await api.patch(`/applications/${appId}/status`, { status });
      setApplications(prev => prev.map(a => a.app_id === appId ? { ...a, status } : a));
      toast.success(`Application ${status}`);
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleMessage = async () => {
    if (!opp) return;
    try {
      await api.post('/messages', { receiver_id: opp.creator_id, content: `Hi! I'm interested in "${opp.title}"` });
      navigate('/messages');
    } catch {
      toast.error('Failed to start chat');
    }
  };

  const handleDeleteOpportunity = async () => {
    setDeleting(true);
    try {
      await api.delete(`/opportunities/${opp.opp_id}`);
      toast.success('Event deleted successfully');
      navigate('/explore');
    } catch {
      toast.error('Failed to delete event');
    }
    setDeleting(false);
    setShowDeleteConfirm(false);
  };

  const handleReport = async () => {
    try {
      await api.post('/reports', { reported_id: opp.opp_id, type: 'opportunity', reason: 'Suspicious posting' });
      toast.success('Report submitted');
    } catch {
      toast.error('Failed to report');
    }
  };

  const openReview = (app) => {
    setReviewTarget(app);
    setReviewRating(5);
    setReviewComment('');
    setShowReview(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewTarget || !reviewComment.trim()) {
      toast.error('Please write a comment');
      return;
    }
    setSubmittingReview(true);
    try {
      await api.post('/reviews', {
        reviewee_id: reviewTarget.applicant_id,
        opp_id: id,
        rating: reviewRating,
        comment: reviewComment,
      });
      toast.success('Review submitted!');
      setShowReview(false);
      setReviewedApps(prev => new Set([...prev, reviewTarget.app_id]));
      setReviewTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    }
    setSubmittingReview(false);
  };

  if (loading) return (
    <div className="px-4 pt-6 max-w-lg mx-auto">
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-100 rounded w-1/3" />
        <div className="h-8 bg-gray-100 rounded w-3/4" />
        <div className="h-40 bg-gray-100 rounded-3xl" />
      </div>
    </div>
  );

  if (!opp) return null;

  const isOwner = user?.user_id === opp.creator_id;
  const catColor = getCategoryColor(opp.category);
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  return (
    <div className="px-4 pt-6 max-w-lg mx-auto pb-8" data-testid="opportunity-details">
      <SEO
        title={opp?.title || 'Opportunity Details'}
        description={opp?.description ? opp.description.slice(0, 160) : 'View opportunity details, apply, and connect with the organization on TeenHands.'}
        path={`/opportunities/${id}`}
      />
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#5C6B7F] font-bold mb-4" data-testid="back-button">
        <ArrowLeft size={20} /> Back
      </button>

      <div className="animate-fade-in-up">
        {/* Cover Image */}
        {opp.image_path && (
          <img
            src={`${API_URL}/files/${opp.image_path}`}
            alt={opp.title}
            className="w-full h-48 object-cover rounded-3xl mb-4 border-2 border-gray-100"
            data-testid="opp-cover-image"
          />
        )}

        {/* Category & Type */}
        <div className="flex items-center gap-2 mb-3">
          <span className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: catColor }}>
            {getCategoryLabel(opp.category)}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${opp.type === 'paid' ? 'bg-[#FF9F1C] text-white' : 'bg-[#2EC4B6]/10 text-[#2EC4B6]'}`}>
            {opp.type === 'paid' ? `$${opp.pay_amount}` : 'Volunteer'}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${opp.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {opp.status}
          </span>
        </div>

        <h1 className="text-3xl font-bold text-[#011627] mb-2">{opp.title}</h1>
        <p className="text-[#5C6B7F] font-semibold mb-4">by {opp.creator_name}</p>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            { icon: MapPin, label: opp.location?.name, color: '#2EC4B6' },
            { icon: Calendar, label: opp.end_date ? `${formatDate(opp.date)} - ${formatDate(opp.end_date)}` : formatDate(opp.date), color: '#FF9F1C' },
            { icon: Clock, label: opp.start_time && opp.end_time ? `${opp.start_time} - ${opp.end_time}` : `${opp.duration} hours`, color: '#6C63FF' },
            { icon: Users, label: `${opp.spots - (opp.applications_count || 0)} spots left`, color: '#EC4899' },
          ].map(({ icon: Icon, label, color }, i) => (
            <div key={i} className="bg-white rounded-2xl p-3 flex items-center gap-3 border-2 border-gray-100">
              <Icon size={20} style={{ color }} strokeWidth={2.5} />
              <span className="text-sm font-semibold text-[#011627] truncate">{label}</span>
            </div>
          ))}
          {opp.zip_code && (
            <div className="bg-white rounded-2xl p-3 flex items-center gap-3 border-2 border-gray-100">
              <MapPin size={20} className="text-[#3A86FF]" strokeWidth={2.5} />
              <span className="text-sm font-semibold text-[#011627]">Zip: {opp.zip_code}</span>
            </div>
          )}
          {opp.distance !== undefined && opp.distance !== null && (
            <div className="bg-[#3A86FF]/10 rounded-2xl p-3 flex items-center gap-3 border-2 border-[#3A86FF]/20">
              <MapPin size={20} className="text-[#3A86FF]" strokeWidth={2.5} />
              <span className="text-sm font-bold text-[#3A86FF]">{opp.distance} miles away</span>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="bg-white rounded-3xl p-5 border-2 border-gray-100 mb-4">
          <h3 className="font-bold text-[#011627] mb-2">About this opportunity</h3>
          <p className="text-[#5C6B7F] leading-relaxed whitespace-pre-wrap">{opp.description}</p>
        </div>

        {/* Skills Needed */}
        {opp.skills_needed?.length > 0 && (
          <div className="bg-white rounded-3xl p-5 border-2 border-gray-100 mb-4">
            <h3 className="font-bold text-[#011627] mb-2">Skills Needed</h3>
            <div className="flex flex-wrap gap-2">
              {opp.skills_needed.map(skill => (
                <span key={skill} className="px-3 py-1 bg-[#6C63FF]/10 text-[#6C63FF] rounded-full text-sm font-bold">{skill}</span>
              ))}
            </div>
          </div>
        )}

        {/* Map */}
        {opp.location?.lat && opp.location?.lng && (
          <div className="rounded-3xl overflow-hidden border-2 border-gray-100 mb-6" style={{ height: '200px' }} data-testid="opp-map">
            <MapContainer center={[opp.location.lat, opp.location.lng]} zoom={14} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[opp.location.lat, opp.location.lng]} />
            </MapContainer>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          {!isOwner && user?.role === 'teen' && (
            <>
              <Button
                onClick={() => setShowApply(true)}
                disabled={hasApplied}
                className={`flex-1 rounded-full py-5 font-bold text-lg btn-bounce ${
                  hasApplied ? 'bg-gray-200 text-gray-500' : 'bg-[#2EC4B6] hover:bg-[#25A296] text-white'
                }`}
                data-testid="apply-btn"
              >
                {hasApplied ? 'Applied' : 'Apply Now'}
              </Button>
              <Button
                onClick={handleMessage}
                variant="outline"
                className="rounded-full py-5 border-2 border-gray-200 btn-bounce px-6"
                data-testid="message-btn"
              >
                <MessageCircle size={20} />
              </Button>
            </>
          )}
          {isOwner && (
            <>
              <Button
                onClick={() => navigate(`/post-task?edit=${opp.opp_id}`)}
                className="flex-1 rounded-full py-5 bg-[#6C63FF] hover:bg-[#5B52FF] text-white font-bold btn-bounce"
                data-testid="edit-opp-btn"
              >
                <Pencil size={18} className="mr-2" /> Edit
              </Button>
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="outline"
                className="rounded-full py-5 border-2 border-red-200 text-red-500 hover:bg-red-50 font-bold btn-bounce px-6"
                data-testid="delete-opp-btn"
              >
                <Trash2 size={18} />
              </Button>
            </>
          )}
          <Button
            onClick={handleReport}
            variant="ghost"
            className="rounded-full py-5 text-[#9AA5B1] hover:text-[#E71D36]"
            data-testid="report-btn"
          >
            <Flag size={18} />
          </Button>
        </div>

        {/* Applications (for org owner) */}
        {isOwner && applications.length > 0 && (
          <div className="mb-6">
            <h3 className="font-bold text-[#011627] mb-3">Applications ({applications.length})</h3>
            <div className="space-y-3">
              {applications.map(app => (
                <div key={app.app_id} className="bg-white rounded-2xl p-4 border-2 border-gray-100" data-testid={`application-${app.app_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => navigate(`/profile/${app.applicant_id}`)}
                      className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                      data-testid={`view-applicant-${app.app_id}`}
                    >
                      {app.applicant_picture ? (
                        <img src={app.applicant_picture} alt={app.applicant_name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#2EC4B6] flex items-center justify-center text-white text-sm font-bold">
                          {app.applicant_name?.[0]}
                        </div>
                      )}
                      <span className="font-bold text-[#011627] hover:text-[#2EC4B6] transition-colors flex items-center gap-1">
                        {app.applicant_name}
                        <ExternalLink size={12} className="text-[#9AA5B1]" />
                      </span>
                    </button>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      app.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      app.status === 'declined' ? 'bg-red-100 text-red-700' :
                      app.status === 'completed' ? 'bg-[#6C63FF]/10 text-[#6C63FF]' :
                      'bg-[#FF9F1C]/10 text-[#FF9F1C]'
                    }`}>
                      {app.status}
                    </span>
                  </div>
                  {app.message && <p className="text-sm text-[#5C6B7F] mb-3">{app.message}</p>}

                  {/* Pending: Accept/Decline */}
                  {app.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm" onClick={() => handleApplicationStatus(app.app_id, 'accepted')}
                        className="flex-1 rounded-full bg-[#2EC4B6] hover:bg-[#25A296] text-white text-sm font-bold"
                        data-testid={`accept-${app.app_id}`}
                      >
                        <CheckCircle size={16} className="mr-1" /> Accept
                      </Button>
                      <Button
                        size="sm" variant="outline" onClick={() => handleApplicationStatus(app.app_id, 'declined')}
                        className="flex-1 rounded-full border-2 text-sm font-bold"
                        data-testid={`decline-${app.app_id}`}
                      >
                        <XCircle size={16} className="mr-1" /> Decline
                      </Button>
                    </div>
                  )}

                  {/* Accepted: Mark Complete */}
                  {app.status === 'accepted' && (
                    <Button
                      size="sm" onClick={() => handleApplicationStatus(app.app_id, 'completed')}
                      className="w-full rounded-full bg-[#6C63FF] hover:bg-[#5B52FF] text-white text-sm font-bold"
                      data-testid={`complete-${app.app_id}`}
                    >
                      <CheckCircle size={16} className="mr-1" /> Mark Complete
                    </Button>
                  )}

                  {/* Completed: Leave Review (only if not already reviewed) */}
                  {app.status === 'completed' && !reviewedApps.has(app.app_id) && (
                    <Button
                      size="sm" onClick={() => openReview(app)}
                      className="w-full rounded-full bg-[#FF9F1C] hover:bg-[#E58E19] text-white text-sm font-bold"
                      data-testid={`review-${app.app_id}`}
                    >
                      <Star size={16} className="mr-1" /> Leave Review for {app.applicant_name}
                    </Button>
                  )}
                  {app.status === 'completed' && reviewedApps.has(app.app_id) && (
                    <div className="w-full text-center text-sm font-bold text-[#9AA5B1] py-2" data-testid={`reviewed-${app.app_id}`}>
                      <CheckCircle size={16} className="inline mr-1 text-[#2EC4B6]" /> Review Submitted
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Apply Dialog */}
      <Dialog open={showApply} onOpenChange={setShowApply}>
        <DialogContent className="rounded-3xl border-2 border-gray-100 max-w-md" data-testid="apply-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Apply for {opp.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Textarea
              placeholder="Why are you interested? (optional)"
              value={message} onChange={e => setMessage(e.target.value)}
              className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl font-semibold min-h-[120px]"
              data-testid="apply-message"
            />
            <Button
              onClick={handleApply}
              className="w-full rounded-full py-5 bg-[#2EC4B6] hover:bg-[#25A296] text-white font-bold text-lg btn-bounce"
              data-testid="submit-application"
            >
              <Send size={18} className="mr-2" /> Send Application
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={showReview} onOpenChange={setShowReview}>
        <DialogContent className="rounded-3xl border-2 border-gray-100 max-w-md" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              Review {reviewTarget?.applicant_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-[#5C6B7F] font-semibold">
              How did {reviewTarget?.applicant_name} perform on &ldquo;{opp?.title}&rdquo;?
            </p>

            {/* Star Rating */}
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setReviewRating(s)}
                  className="transition-transform hover:scale-110"
                  data-testid={`rating-star-${s}`}
                >
                  <Star
                    size={36}
                    className={s <= reviewRating ? 'text-[#FF9F1C] fill-[#FF9F1C]' : 'text-gray-300'}
                    strokeWidth={2}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-sm font-bold text-[#5C6B7F]">
              {reviewRating === 1 && 'Needs Improvement'}
              {reviewRating === 2 && 'Below Average'}
              {reviewRating === 3 && 'Average'}
              {reviewRating === 4 && 'Great Job'}
              {reviewRating === 5 && 'Outstanding!'}
            </p>

            <Textarea
              placeholder="Share your experience working with this teen..."
              value={reviewComment}
              onChange={e => setReviewComment(e.target.value)}
              className="bg-gray-50 border-2 border-gray-100 focus:border-[#FF9F1C] rounded-2xl font-semibold min-h-[100px]"
              data-testid="review-comment"
            />

            <Button
              onClick={handleSubmitReview}
              disabled={submittingReview || !reviewComment.trim()}
              className="w-full rounded-full py-5 bg-[#FF9F1C] hover:bg-[#E58E19] text-white font-bold text-lg btn-bounce"
              data-testid="submit-review"
            >
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-3xl border-2 border-gray-100 max-w-md" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#011627]">Delete this event?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-[#5C6B7F] font-semibold">
              Are you sure you want to delete <strong>{opp?.title}</strong>? This action cannot be undone. All applications will also be removed.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="outline"
                className="flex-1 rounded-full py-5 border-2 font-bold"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteOpportunity}
                disabled={deleting}
                className="flex-1 rounded-full py-5 bg-red-500 hover:bg-red-600 text-white font-bold"
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
